import React from 'react';
import { Alert, Box, Button, ProgressBar, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';

export interface Step1ConnectWorkDocsProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  onGetToken: () => void;
  /** True while the folder tree is being provisioned (auto-runs after connecting). */
  isProvisioning: boolean;
  /** Live provisioning progress (folders created / total). */
  provisioningProgress: { current: number; total: number };
  /** Provisioning error, if any. */
  provisioningError: string | null;
  /**
   * True only when `provisioningError` is a WorkDocs token expiry (vs a generic partial failure).
   * When set, the error is shown with a "Reconnect WorkDocs" action instead of a plain status line.
   */
  provisioningTokenExpired?: boolean;
  /**
   * Non-blocking provisioning notice, if any — e.g. the org exceeded the cap so only direct-report
   * folders were created. Shown as a warning; does not block advancing.
   */
  provisioningWarning?: string | null;
  /** True once this quarter's root folder exists (provisioned or adopted). */
  isProvisioned: boolean;
  /**
   * Re-initiates the WorkDocs OAuth flow (same handler as `onGetToken`). Wired to the reconnect
   * action shown on a token-expiry provisioning error. On return, provisioning auto-resumes.
   */
  onReconnect?: () => void;
}

/**
 * Step 1: acquire a WorkDocs access token via the PKCE OAuth redirect, then AUTO-provision the
 * folder tree (no manual "Set Up" button — the page kicks off provisioning as soon as the token
 * lands). The wizard gates advancement on both the token AND the provisioned root, so this step
 * surfaces the connect state plus the live provisioning progress/error. On clean success the page
 * auto-advances to Step 2.
 */
export const Step1ConnectWorkDocs: React.FC<Step1ConnectWorkDocsProps> = ({
  isAuthenticated,
  isLoading,
  error,
  onGetToken,
  isProvisioning,
  provisioningProgress,
  provisioningError,
  provisioningTokenExpired,
  provisioningWarning,
  isProvisioned,
  onReconnect
}) => (
  <SpaceBetween size="m">
    {isAuthenticated ? (
      <StatusIndicator type="success">WorkDocs Connected</StatusIndicator>
    ) : (
      <Box>
        <Button variant="primary" iconName="lock-private" onClick={onGetToken} loading={isLoading}>
          Get WorkDocs Token
        </Button>
      </Box>
    )}

    {error && <StatusIndicator type="error">{error}</StatusIndicator>}

    {/* Folder provisioning runs automatically once connected. Show its progress, then a
        confirmation once the folder tree is ready (the wizard advances to Step 2 on success). */}
    {isAuthenticated && isProvisioning && (
      <Box>
        <ProgressBar
          value={provisioningProgress.total > 0 ? (provisioningProgress.current / provisioningProgress.total) * 100 : 0}
          additionalInfo={`${provisioningProgress.current} / ${provisioningProgress.total} folders`}
          variant="key-value"
          label="Setting Up WorkDocs Folders"
        />
      </Box>
    )}

    {isAuthenticated && !isProvisioning && isProvisioned && <StatusIndicator type="success">WorkDocs Folders Ready</StatusIndicator>}

    {provisioningWarning && <StatusIndicator type="warning">{provisioningWarning}</StatusIndicator>}

    {/* A token-expiry error gets a one-click reconnect (re-runs the same OAuth flow as
        "Get WorkDocs Token"); provisioning auto-resumes on return. Any other provisioning
        failure stays a plain status line. */}
    {provisioningError &&
      (provisioningTokenExpired && onReconnect ? (
        <Alert
          type="error"
          action={
            <Button iconName="lock-private" onClick={onReconnect}>
              Reconnect WorkDocs
            </Button>
          }
        >
          {provisioningError}
        </Alert>
      ) : (
        <StatusIndicator type="error">{provisioningError}</StatusIndicator>
      ))}
  </SpaceBetween>
);
